import React from "react";
import { App, Button, Card, Col, InputNumber, Row, Segmented, Space, Typography } from "antd";
import { useAgentEntitlements } from "../../../features/agentEntitlements/useAgentEntitlements";
import { useAgentPublicSitePricingSettings } from "../../../features/agentEntitlements/useAgentPublicSitePricingSettings";
import {
  buildPublicSiteCommissionSavedToast,
  formatCommissionWillSaveAs,
} from "../../../utils/agentPublicSitePricingSettingsMapper";

const { Text, Title } = Typography;

/** Segmented הוא controlled — תמיד fixed|percent מהשרת כדי למניעת מצב ללא התאמת אופציות */
function coerceSegmentType(t) {
  return t === "percent" || t === "fixed" ? t : "fixed";
}

function PublicSiteCommissionsSettingsContent() {
  const { message } = App.useApp();
  const {
    publicSitePricingByCategory,
    loading: commissionLoading,
    save: saveCommissionSettings,
  } = useAgentPublicSitePricingSettings();
  const [hotelType, setHotelType] = React.useState("fixed");
  const [hotelValue, setHotelValue] = React.useState(0);
  const [flightType, setFlightType] = React.useState("fixed");
  const [flightValue, setFlightValue] = React.useState(0);
  const [packageType, setPackageType] = React.useState("fixed");
  const [packageValue, setPackageValue] = React.useState(0);

  React.useEffect(() => {
    const h = publicSitePricingByCategory?.hotel;
    const f = publicSitePricingByCategory?.flight;
    const p = publicSitePricingByCategory?.vacation_package;
    setHotelType(coerceSegmentType(h?.type));
    setHotelValue(Number(h?.value) || 0);
    setFlightType(coerceSegmentType(f?.type));
    setFlightValue(Number(f?.value) || 0);
    setPackageType(coerceSegmentType(p?.type));
    setPackageValue(Number(p?.value) || 0);
  }, [publicSitePricingByCategory]);

  const saveFixedCommissions = async () => {
    const hotelT = coerceSegmentType(hotelType);
    const flightT = coerceSegmentType(flightType);
    const packageT = coerceSegmentType(packageType);
    const entries = [
      { key: "מלונות", type: hotelT, value: Number(hotelValue) },
      { key: "טיסות", type: flightT, value: Number(flightValue) },
      { key: "חבילות נופש", type: packageT, value: Number(packageValue) },
    ];
    const invalid = entries.find(
      (entry) =>
        !Number.isFinite(entry.value) ||
        entry.value < 0 ||
        !["fixed", "percent"].includes(entry.type) ||
        (entry.type === "percent" && entry.value > 100)
    );
    if (invalid) {
      message.error("נא להזין מספרים חוקיים (0 ומעלה).");
      return;
    }
    try {
      const payload = {
        hotel: { type: hotelT, value: Number(hotelValue) || 0 },
        flight: { type: flightT, value: Number(flightValue) || 0 },
        vacation_package: { type: packageT, value: Number(packageValue) || 0 },
      };
      const data = await saveCommissionSettings(payload);
      message.success(buildPublicSiteCommissionSavedToast(data ?? publicSitePricingByCategory));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "שגיאה בשמירת עמלות האתר";
      message.error(String(msg));
    }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <Title level={5} style={{ marginBottom: 12 }}>
        הגדרות עמלות לאתר הפומבי
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        עמלה שתתווסף למחיר שמוצג ללקוח באתר — כסכום קבוע בשקלים או באחוזים מבסיס המחיר שהגדרנו למערכת.
      </Text>
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Text style={{ display: "block", marginBottom: 6 }}>
            עמלה למלונות באתר
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Segmented
              options={[
                { label: "סכום קבוע ₪", value: "fixed" },
                { label: "אחוז %", value: "percent" },
              ]}
              value={coerceSegmentType(hotelType)}
              onChange={(v) => setHotelType(coerceSegmentType(v))}
            />
            <InputNumber
              min={0}
              max={coerceSegmentType(hotelType) === "percent" ? 100 : undefined}
              step={coerceSegmentType(hotelType) === "percent" ? 0.1 : 1}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
              addonAfter={coerceSegmentType(hotelType) === "percent" ? "%" : "₪"}
              value={hotelValue}
              onChange={(v) => setHotelValue(v ?? 0)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {coerceSegmentType(hotelType) === "percent"
                ? "אחוז שיתווסף למחיר הסופי"
                : "סכום קבוע שיתווסף למחיר הסופי"}
            </Text>
            <Text type="success" style={{ fontSize: 12 }}>
              {formatCommissionWillSaveAs(coerceSegmentType(hotelType), hotelValue)}
            </Text>
          </Space>
        </Col>
        <Col xs={24}>
          <Text style={{ display: "block", marginBottom: 6 }}>
            עמלה לטיסות באתר
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Segmented
              options={[
                { label: "סכום קבוע ₪", value: "fixed" },
                { label: "אחוז %", value: "percent" },
              ]}
              value={coerceSegmentType(flightType)}
              onChange={(v) => setFlightType(coerceSegmentType(v))}
            />
            <InputNumber
              min={0}
              max={coerceSegmentType(flightType) === "percent" ? 100 : undefined}
              step={coerceSegmentType(flightType) === "percent" ? 0.1 : 1}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
              addonAfter={coerceSegmentType(flightType) === "percent" ? "%" : "₪"}
              value={flightValue}
              onChange={(v) => setFlightValue(v ?? 0)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {coerceSegmentType(flightType) === "percent"
                ? "אחוז שיתווסף למחיר הסופי"
                : "סכום קבוע שיתווסף למחיר הסופי"}
            </Text>
            <Text type="success" style={{ fontSize: 12 }}>
              {formatCommissionWillSaveAs(coerceSegmentType(flightType), flightValue)}
            </Text>
          </Space>
        </Col>
        <Col xs={24}>
          <Text style={{ display: "block", marginBottom: 6 }}>
            עמלה לחבילות נופש באתר
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Segmented
              options={[
                { label: "סכום קבוע ₪", value: "fixed" },
                { label: "אחוז %", value: "percent" },
              ]}
              value={coerceSegmentType(packageType)}
              onChange={(v) => setPackageType(coerceSegmentType(v))}
            />
            <InputNumber
              min={0}
              max={coerceSegmentType(packageType) === "percent" ? 100 : undefined}
              step={coerceSegmentType(packageType) === "percent" ? 0.1 : 1}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
              addonAfter={coerceSegmentType(packageType) === "percent" ? "%" : "₪"}
              value={packageValue}
              onChange={(v) => setPackageValue(v ?? 0)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {coerceSegmentType(packageType) === "percent"
                ? "אחוז שיתווסף למחיר הסופי"
                : "סכום קבוע שיתווסף למחיר הסופי"}
            </Text>
            <Text type="success" style={{ fontSize: 12 }}>
              {formatCommissionWillSaveAs(coerceSegmentType(packageType), packageValue)}
            </Text>
          </Space>
        </Col>
        <Col xs={24}>
          <Button
            type="primary"
            onClick={saveFixedCommissions}
            loading={commissionLoading}
          >
            שמירת עמלות האתר
          </Button>
        </Col>
      </Row>
    </Card>
  );
}

/**
 * עמלות אתר ציבורי — רק עם entitlement agent_public_site (SSOT).
 * ללא גישה: לא ב-DOM, בלי נעילה/אפסייל. ללא קריאת API עד לאישור entitlements.
 */
export default function PublicSiteCommissionsSettings() {
  const {
    loading: entitlementsLoading,
    status: entitlementsStatus,
    canUseAgentPublicSite,
    data,
  } = useAgentEntitlements();

  const packageCode = data?.package?.code ?? null;

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (entitlementsLoading) return;
    if (entitlementsStatus !== "ok") return;
    if (canUseAgentPublicSite) return;
    console.info(
      "[FEATURE_SECTION_HIDDEN]",
      JSON.stringify({
        section: "public_site_commission_settings",
        packageCode,
        allowed: canUseAgentPublicSite,
      })
    );
  }, [
    entitlementsLoading,
    entitlementsStatus,
    canUseAgentPublicSite,
    packageCode,
  ]);

  if (entitlementsLoading || entitlementsStatus !== "ok") {
    return null;
  }
  if (!canUseAgentPublicSite) {
    return null;
  }

  return <PublicSiteCommissionsSettingsContent />;
}
